import React, { useState } from 'react';
import { Mail, Loader, CheckCircle, XCircle } from 'lucide-react';

interface NewsletterFormProps {
  className?: string;
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({ className = '' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      // Here we would normally call an API endpoint to handle the subscription
      // For now, we'll simulate an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus('success');
      setMessage('Thanks for subscribing! Check your email for confirmation.');
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again later.');
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="flex flex-col w-full space-y-3 md:space-y-0 md:flex-row md:space-x-2">
        <div className="relative flex-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
            disabled={status === 'loading' || status === 'success'}
            required
          />
          {status !== 'idle' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {status === 'loading' && (
                <Loader className="h-5 w-5 text-purple-400 animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="h-5 w-5 text-green-400" />
              )}
              {status === 'error' && (
                <XCircle className="h-5 w-5 text-red-400" />
              )}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          <Mail className="h-4 w-4" />
          <span>Subscribe</span>
        </button>
      </form>
      
      {message && (
        <div className={`mt-3 text-sm ${
          status === 'success' ? 'text-green-400' : 'text-red-400'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default NewsletterForm;